import {Dispatch, SetStateAction, useState} from 'react';

import {omit} from 'ramda';

import {useBoolean} from 'shared/web';

import {PresetApiStateEnum} from '../constants/PresetApiStateEnum';

const PRESET_API_STATUS_DURATION_MS = 1200;

type UsePresetApiStateProps = {
  updatePreset: (presetId: string) => Promise<void> | void;
  duplicatePreset: (presetId: string) => Promise<void>;
  deletePreset: (presetId: string) => Promise<void>;
  applyPresetToVirtual: (presetId: string, saveCurrentPreset?: boolean) => Promise<void>;
  updatePresetPosition: (from: number, to: number) => Promise<void>;
};

type UsePresetApiStateResult = {
  apiState: Record<string, PresetApiStateEnum>;
  copyState: Record<string, PresetApiStateEnum>;
  isPresetChangeDisabled: boolean;
  updatePresetProxy: (presetId: string) => void;
  duplicatePresetProxy: (presetId: string) => void;
  deletePresetProxy: (presetId: string) => void;
  applyPresetToVirtualProxy: (
    presetId: string,
    saveCurrentPreset?: boolean,
    onSuccess?: () => void
  ) => void;
  updatePresetPositionProxy: (presetId: string, from: number, to: number) => void;
  activatePresetProxy: (presetId: string, operation: () => Promise<void>) => void;
};

type WrapPresetApiCallDeps = {
  setApiState: Dispatch<SetStateAction<Record<string, PresetApiStateEnum>>>;
  disabledPresetChange: () => void;
  allowPresetChange: () => void;
};

type WrapPresetApiCallOptions = {
  /** When true, calls disabledPresetChange() before the operation and allowPresetChange() in finally (async path only). */
  shouldDisableChanges?: boolean;
  /** When true, the cleanup timer removes the key entirely (omit) instead of setting idle. Used by deletePresetProxy. */
  shouldDeleteState?: boolean;
  onSuccess?: () => void;
};

/**
 * Factory that encapsulates the 1200ms timer / loading→success/error→idle (or delete) state
 * machine shared by all 4 preset API proxies.
 *
 * @mandatory The sync-vs-async branch is intentional and required by the API contract:
 * updatePreset may return void synchronously (no HTTP round-trip) — in that case we only
 * schedule the idle reset; we do NOT transition through success/error, and we do NOT call
 * allowPresetChange() (matching the original updatePresetProxy behavior). All other proxies
 * always return a Promise. Do not collapse the branch.
 */
export function wrapPresetApiCall(
  presetId: string,
  operation: () => Promise<void> | void,
  deps: WrapPresetApiCallDeps,
  options: WrapPresetApiCallOptions = {}
): void {
  const {shouldDisableChanges = false, shouldDeleteState = false} = options;
  const {setApiState, disabledPresetChange, allowPresetChange} = deps;

  if (shouldDisableChanges) {
    disabledPresetChange();
  }
  setApiState((prev) => ({...prev, [presetId]: PresetApiStateEnum.loading}));

  const operationResult = operation();

  const promise =
    operationResult != null && typeof operationResult === 'object'
      ? (operationResult as Promise<void>)
      : null;

  if (promise == null) {
    // Sync (void) path — schedule idle timer only; do NOT call allowPresetChange()
    // (preserves original updatePresetProxy behavior)
    setTimeout(
      () => setApiState((prev) => ({...prev, [presetId]: PresetApiStateEnum.idle})),
      PRESET_API_STATUS_DURATION_MS
    );
    return;
  }

  promise
    .then(() => {
      setApiState((prev) => ({...prev, [presetId]: PresetApiStateEnum.success}));
      if (options.onSuccess) {
        setTimeout(options.onSuccess, PRESET_API_STATUS_DURATION_MS);
      }
    })
    .catch(() => setApiState((prev) => ({...prev, [presetId]: PresetApiStateEnum.error})))
    .finally(() => {
      if (shouldDeleteState) {
        setTimeout(
          () => setApiState((prev) => omit([presetId], prev)),
          PRESET_API_STATUS_DURATION_MS
        );
      } else {
        setTimeout(
          () => setApiState((prev) => ({...prev, [presetId]: PresetApiStateEnum.idle})),
          PRESET_API_STATUS_DURATION_MS
        );
      }
      if (shouldDisableChanges) {
        allowPresetChange();
      }
    });
}

/**
 * Manages per-preset API loading/success/error state for CRUD operations.
 *
 * Extracted from PresetPanel to keep the component lean and allow the state
 * machine to be tested independently.
 */
export function usePresetApiState(props: UsePresetApiStateProps): UsePresetApiStateResult {
  const [apiState, setApiState] = useState<Record<string, PresetApiStateEnum>>({});
  const [copyState, setCopyState] = useState<Record<string, PresetApiStateEnum>>({});
  const [isPresetChangeDisabled, disabledPresetChange, allowPresetChange] = useBoolean();

  const deps: WrapPresetApiCallDeps = {setApiState, disabledPresetChange, allowPresetChange};
  const copyDeps: WrapPresetApiCallDeps = {
    ...deps,
    setApiState: (update) => {
      setApiState(update);
      setCopyState(update);
    },
  };

  const updatePresetProxy = (presetId: string) =>
    wrapPresetApiCall(presetId, () => props.updatePreset(presetId), deps, {
      shouldDisableChanges: true,
    });

  const duplicatePresetProxy = (presetId: string) =>
    wrapPresetApiCall(presetId, () => props.duplicatePreset(presetId), deps, {
      shouldDisableChanges: true,
    });

  const deletePresetProxy = (presetId: string) =>
    wrapPresetApiCall(presetId, () => props.deletePreset(presetId), deps, {
      shouldDisableChanges: false,
      shouldDeleteState: true,
    });

  const applyPresetToVirtualProxy = (
    presetId: string,
    saveCurrentPreset?: boolean,
    onSuccess?: () => void
  ) =>
    wrapPresetApiCall(
      presetId,
      () => props.applyPresetToVirtual(presetId, saveCurrentPreset),
      copyDeps,
      {shouldDisableChanges: true, onSuccess}
    );

  const updatePresetPositionProxy = (presetId: string, from: number, to: number) =>
    wrapPresetApiCall(presetId, () => props.updatePresetPosition(from, to), deps, {
      shouldDisableChanges: true,
    });

  /**
   * Activation (switching to a preset, or back to the virtual "Default" view) is
   * NOT a CRUD mutation, so it does NOT go through wrapPresetApiCall: there is no
   * success/error confirmation to show — the pill simply becomes active. We only
   * pin the target pill to `loading` (→ spinner) and disable the others for the
   * duration, then reset to `idle` on settle.
   */
  const activatePresetProxy = (presetId: string, operation: () => Promise<void>) => {
    setApiState((prev) => ({...prev, [presetId]: PresetApiStateEnum.loading}));
    disabledPresetChange();
    operation().finally(() => {
      allowPresetChange();
      setApiState((prev) => ({...prev, [presetId]: PresetApiStateEnum.idle}));
    });
  };

  return {
    apiState,
    copyState,
    isPresetChangeDisabled,
    updatePresetProxy,
    duplicatePresetProxy,
    deletePresetProxy,
    applyPresetToVirtualProxy,
    updatePresetPositionProxy,
    activatePresetProxy,
  };
}
